import { useState, useEffect } from 'react';
import { 
  UserCog, 
  Plus,
  Edit,
  Shield,
  Check,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { adminsAPI, authAPI } from '../lib/api';
import { formatDateTime, getInitials } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const roleLabels = {
  super_admin: 'Super Admin',
  finance_admin: 'Finance Admin',
  support_admin: 'Support Admin',
  kyc_officer: 'KYC Officer',
  risk_analyst: 'Risk Analyst',
  marketing_admin: 'Marketing Admin',
};

const roleColors = {
  super_admin: 'bg-primary/20 text-primary',
  finance_admin: 'bg-emerald-500/20 text-emerald-400',
  support_admin: 'bg-blue-500/20 text-blue-400',
  kyc_officer: 'bg-violet-500/20 text-violet-400',
  risk_analyst: 'bg-amber-500/20 text-amber-400',
  marketing_admin: 'bg-pink-500/20 text-pink-400',
};

export default function AdminRoles() {
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    name: '',
    role: 'support_admin',
  });

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminsAPI.getAll();
      setAdmins(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Permission denied');
      } else {
        toast.error('Failed to fetch admins');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreate = async () => {
    try {
      await authAPI.register(newAdmin);
      toast.success('Admin created successfully');
      setCreateDialog(false);
      setNewAdmin({ email: '', password: '', name: '', role: 'support_admin' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleToggleActive = async (adminId, isActive) => {
    try {
      await adminsAPI.update(adminId, { is_active: !isActive });
      toast.success('Admin updated');
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to update admin');
    }
  };

  const handleRoleChange = async (adminId, newRole) => {
    try {
      await adminsAPI.update(adminId, { role: newRole });
      toast.success('Role updated');
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const isSuperAdmin = currentAdmin?.role === 'super_admin';

  return (
    <div data-testid="admins-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Admin Management</h1>
          <p className="page-subtitle">Manage admin users and permissions</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setCreateDialog(true)} data-testid="create-admin-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Admin
          </Button>
        )}
      </div>

      {/* Role Permissions Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Object.entries(roleLabels).map(([role, label]) => (
          <div key={role} className="stat-card">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${roleColors[role]}`}>
              <Shield className="w-5 h-5" />
            </div>
            <p className="font-medium text-sm">{label}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {admins.filter(a => a.role === role).length} users
            </p>
          </div>
        ))}
      </div>

      {/* Admins Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <h3 className="data-table-title">Admin Users</h3>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Admin</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead>Status</TableHead>
              {isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  {isSuperAdmin ? 'No admins found' : 'Permission denied to view admins'}
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id} className="table-row" data-testid={`admin-row-${admin.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold ${roleColors[admin.role]}`}>
                        {getInitials(admin.name)}
                      </div>
                      <div>
                        <p className="font-medium">{admin.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{admin.id.slice(0, 8)}...</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    {isSuperAdmin && admin.id !== currentAdmin?.id ? (
                      <Select
                        value={admin.role}
                        onValueChange={(v) => handleRoleChange(admin.id, v)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(roleLabels).map(([role, label]) => (
                            <SelectItem key={role} value={role}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`badge ${roleColors[admin.role]}`}>
                        {roleLabels[admin.role]}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {admin.last_login ? formatDateTime(admin.last_login) : 'Never'}
                  </TableCell>
                  <TableCell>
                    <span className={`badge ${admin.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-right">
                      {admin.id !== currentAdmin?.id && (
                        <Switch
                          checked={admin.is_active}
                          onCheckedChange={() => handleToggleActive(admin.id, admin.is_active)}
                          data-testid={`toggle-admin-${admin.id}`}
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Admin Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Enter name"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="Enter email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Enter password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                data-testid="admin-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={newAdmin.role}
                onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}
              >
                <SelectTrigger data-testid="admin-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleLabels).map(([role, label]) => (
                    <SelectItem key={role} value={role}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!newAdmin.name || !newAdmin.email || !newAdmin.password}
              data-testid="confirm-create-admin-btn"
            >
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
